// Otomatik boyutlandırma: Web Worker'da çalışır (yoksa — jsdom/test — senkron).
// Durum modül düzeyinde (zustand): pafta değişip Akıllı tasarım ekranı kapansa da son karşılaştırma ve girdiler korunur
// (Cem 2026-09-29: şeritte sekmeye tıklayınca sonuç kayboluyordu).
import { create } from "zustand";
import { autoSizeWall, type AutoSizeOptions, type AutoSizeReport, type WallInput } from "@tasduvar/engine";
import type { AutoSizeRequest, AutoSizeResponse } from "./autosize.worker";

export interface AutoSizeState {
  status: "idle" | "running" | "done" | "error";
  report: AutoSizeReport | null;
  error: string | null;
  progress: { done: number; total: number } | null;
  /** Sonucun ait olduğu duvar ve o anki girdi özeti (duvar sonradan değiştiyse "güncel değil") */
  wallId: string | null;
  inputKey: string | null;
  /** Akıllı tasarım paletinin yerel girdileri (duvar başına) */
  inputs: Record<string, { height: number; length: number }>;
  /** Akıllı başlangıç: pafta açılınca bu duvar için tarama başlatılsın */
  pending: string | null;
  /**
   * Akıllı başlangıçtan gelen taze tarama (duvar henüz hiç boyutlandırılmadı): sonuç gelince en ekonomik kurtaran aday
   * kendiliğinden duvara uygulanır ve "mevcut duvar" kıyası gösterilmez. Uygulanınca / elle "Boyutlandır"da null olur.
   */
  fresh: string | null;
}
interface Store extends AutoSizeState {
  run(wall: WallInput, options: Omit<AutoSizeOptions, "onProgress">): void;
  reset(): void;
  setInputs(wallId: string, v: { height: number; length: number }): void;
  /** `wallId` için pafta açılınca tarama başlat (null = istek tüketildi); `fresh: true` → akıllı başlangıç (otomatik uygula) */
  requestRun(wallId: string | null, opts?: { fresh?: boolean }): void;
  setFresh(wallId: string | null): void;
}

let worker: Worker | null = null;
let seq = 0;
/** Sonucu etkileyen duvar girdilerinin özeti (çizim/metraj ayarları hariç) */
export const autoSizeInputKey = (w: WallInput, height: number): string =>
  JSON.stringify([height, w.backfill, w.foundationSoil, w.water, w.loads, w.seismic, w.checks, w.geometry.embedmentDepth, w.propertyLine ?? null, w.rc ?? null, w.material]);   // rc/malzeme: tarama sonrası donatı/malzeme değişirse sonuç güncel değil (2026-10-06 tarama)

export const useAutoSizeStore = create<Store>((set) => ({
  status: "idle", report: null, error: null, progress: null, wallId: null, inputKey: null, inputs: {}, pending: null, fresh: null,
  requestRun: (pending, opts) => set(pending === null ? { pending } : { pending, fresh: opts?.fresh ? pending : null }),
  setFresh: (fresh) => set({ fresh }),
  setInputs: (wallId, v) => set((s) => ({ inputs: { ...s.inputs, [wallId]: v } })),
  reset: () => { seq++; set({ status: "idle", report: null, error: null, progress: null, wallId: null, inputKey: null, fresh: null }); },
  run: (wall, options) => {
    const id = ++seq;
    set({ status: "running", report: null, error: null, progress: null, wallId: wall.id, inputKey: autoSizeInputKey(wall, options.height) });
    if (typeof Worker === "undefined") {
      try { set({ status: "done", report: autoSizeWall(wall, options), error: null, progress: null }); }
      catch (e) { set({ status: "error", report: null, error: e instanceof Error ? e.message : "Boyutlandırma yapılamadı", progress: null }); }
      return;
    }
    if (!worker) {
      worker = new Worker(new URL("./autosize.worker.ts", import.meta.url), { type: "module" });
      worker.onmessage = (e: MessageEvent<AutoSizeResponse>): void => {
        const m = e.data;
        if (m.id !== seq) return;
        if ("progress" in m) set((s) => (s.status === "running" ? { progress: m.progress } : {}));
        else if ("report" in m) set({ status: "done", report: m.report, error: null, progress: null });
        else set({ status: "error", report: null, error: m.error, progress: null });
      };
      worker.onerror = (): void => { set({ status: "error", report: null, error: "Boyutlandırma işçisi çalıştırılamadı", progress: null }); worker?.terminate(); worker = null; };
    }
    worker.postMessage({ id, wall, options } satisfies AutoSizeRequest);
  },
}));

/** Eski arayüz: bileşenler aynı durumu paylaşır */
export function useAutoSize(): AutoSizeState & { run: Store["run"]; reset: Store["reset"]; requestRun: Store["requestRun"]; setFresh: Store["setFresh"] } {
  return useAutoSizeStore();
}
